import {
	animalBaggageTypes,
	getAiPurchasePopupGoal,
	getBaggageDatalayerName,
	getFunnelParameters,
	resolveAgencyByGraphqlUrl,
	getServiceCategory,
	getTravellersTypes
} from './utils';
import {
	CheckinDocumentsQuery,
	CheckinOrderFragment,
	CheckinOrderFragmentDoc,
	CheckinOrderStatus,
	CheckoutOrderFragment,
	CheckoutOrderFragmentDoc,
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment,
	NASProductType,
	OrderAdditionalServiceGdsServiceBaggage,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderStatus,
	SearchParametersFragment
} from '@websky/graphql';
import {
	ApolloResponse,
	CheckoutCheckinOrder,
	DataLayerItemsListName,
	ItemCategory,
	PurchaseShowPopupAction,
	WxEvent,
	WxEventOptions,
	WxEvents
} from './types';
import { DataLayerService } from './OrderDataLayer/types';
import { ApolloClient, NormalizedCacheObject, Operation } from 'apollo-boost';
import getOrderDataLayer from './orderDataLayerInstance';
import WebskyServicesAnalytics from './WebskyServicesAnalytics';
import { isFareLockService } from '../ConfirmOption/utils';
import Gds from './Diff/Gds';
import Aeroexpress from './Diff/Aeroexpress';
import Seat from './Diff/Seat';
import Nas from './Diff/Nas';
import { getHoldDatalayerItem } from './Diff/utils';
import { convertBaggageObject } from '../Checkout/types';
import { CheckinGoal, Goal, InsuranceGoal, PurchaseGoal, reachGoal } from '../analytics';
import { Transliteration } from '../transliteration';
import { get, getCurrency, getLocaleFromLocalStorage, SEARCH_FORM_STATE_KEY } from '../cache';
import { ChangedService } from './Diff/types';
import { PixelMessageBuilder, PixelEventService, OrderEventType } from '@websky/pixel';
import { CachedSearchFormState } from '../SearchForm/types';
import { EventMessage, EventType } from '@websky/pixel';
import { Log } from '../sentry';

class WebskyAnalyticsApollo {
	private static readonly wxView = 'wxView';
	private static previousOrderState: CheckoutCheckinOrder = null;
	private static usePixel: boolean = false;
	private static graphqlUrl: string = null;
	private static readonly pixelService: PixelEventService = PixelEventService.getInstance();

	private static async sendPixelEvent(event: EventMessage) {
		if (!this.usePixel) {
			return;
		}

		try {
			await WebskyAnalyticsApollo.pixelService.sendEvent(event);
		} catch (error) {
			Log({
				level: 'error',
				message: `Error sending pixel event: ${String(error)}`
			});
		}
	}

	/**
	 * Хелпер для построения и отправки Order событий с автоматическим получением cdpId
	 */
	private static sendOrderEvent(
		eventType: OrderEventType,
		order: CheckoutOrderFragment | CheckinOrderFragment,
		includeServices?: {
			serviceId: string;
			segmentIds: string[];
			passengerIds: string[];
		}[]
	): void {
		const cdpId = WebskyAnalyticsApollo.pixelService.getCdpId();
		if (cdpId) {
			const customerArgs = {
				agency: resolveAgencyByGraphqlUrl(this.graphqlUrl),
				language: getLocaleFromLocalStorage()
			};

			WebskyAnalyticsApollo.sendPixelEvent(
				PixelMessageBuilder.buildOrderEvent(eventType, order, cdpId, customerArgs, includeServices)
			);
		}
	}

	/**
	 * Хелпер для построения и отправки Search событий с автоматическим получением cdpId
	 */
	private static sendSearchEvent(
		searchParams: SearchParametersFragment,
		searchFormState: CachedSearchFormState
	): void {
		const cdpId = WebskyAnalyticsApollo.pixelService.getCdpId();
		if (cdpId) {
			WebskyAnalyticsApollo.sendPixelEvent(
				PixelMessageBuilder.buildSearchEvent(searchParams, searchFormState, cdpId)
			);
		}
	}

	private static readonly readCachedOrder = (
		client: ApolloClient<NormalizedCacheObject>,
		orderId: string,
		type: 'checkout' | 'checkin'
	): CheckoutCheckinOrder => {
		const params = {
			fragment: type === 'checkout' ? CheckoutOrderFragmentDoc : CheckinOrderFragmentDoc,
			fragmentName: type === 'checkout' ? 'CheckoutOrder' : 'CheckinOrder',
			id: type === 'checkout' ? `AviaOrder:${orderId}` : `CheckinOrder:${orderId}`
		};

		try {
			const order = client.readFragment(params);

			return order;
		} catch (e) {
			return null;
		}
	};

	private static isCheckoutOrderGuard(order: CheckoutCheckinOrder): order is CheckoutOrderFragment {
		return order?.hasOwnProperty('flight');
	}

	private static readonly isQueryAfterOrderWasPaid = (orderAfterChange: CheckoutOrderFragment) => {
		const orderBeforeChange = this.previousOrderState;

		return orderBeforeChange?.status === OrderStatus.Booked && orderAfterChange?.status === OrderStatus.Confirmed;
	};

	private static readonly isQueryAfterServicesWerePaid = (orderAfterChange: CheckoutOrderFragment) => {
		const orderBeforeChange = this.previousOrderState;

		const unpaidServicesBeforeChange = WebskyServicesAnalytics.getCheckoutSelectedServices(
			orderBeforeChange as CheckoutOrderFragment
		);
		const unpaidServicesAfterChange = WebskyServicesAnalytics.getCheckoutSelectedServices(orderAfterChange);

		return (
			orderBeforeChange?.status === OrderStatus.Confirmed &&
			orderAfterChange?.status === OrderStatus.Confirmed &&
			unpaidServicesBeforeChange.length &&
			!unpaidServicesAfterChange.length
		);
	};

	private static analyzeOrderQuery(orderAfterChange: CheckoutOrderFragment) {
		const orderBeforeChange = this.previousOrderState;
		const extraServicesCategories = [
			ItemCategory.Seat,
			ItemCategory.Baggage,
			ItemCategory.Meal,
			ItemCategory.Insurance,
			ItemCategory.Aeroexpress,
			ItemCategory.Pets,
			ItemCategory.Business
		];

		if (this.isQueryAfterOrderWasPaid(orderAfterChange) || this.isQueryAfterServicesWerePaid(orderAfterChange)) {
			const selectedServices = WebskyServicesAnalytics.getCheckoutSelectedServices(
				orderBeforeChange as CheckoutOrderFragment // orderBeforeChange because we need services and order.priceToPay.airticket?.amount
			);

			const selectedExtraServices = selectedServices.filter(service =>
				extraServicesCategories.includes(service.category)
			);

			getOrderDataLayer().purchaseOrder(orderAfterChange.locator, selectedServices);

			if (selectedExtraServices.length) {
				reachGoal(PurchaseGoal.PurchaseWithExtraServices, {
					order_price: selectedExtraServices.length,
					currency: getCurrency()
				});
			}
		}

		if (this.isQueryAfterOrderWasPaid(orderAfterChange)) {
			const selectedInsurances = WebskyServicesAnalytics.getCheckoutSelectedServices(
				orderBeforeChange as CheckoutOrderFragment
			).filter(service => service.category === ItemCategory.Insurance);

			if (selectedInsurances.length) {
				const insurancesToAnalytics = selectedInsurances
					.map(item => Transliteration.cyrillicToLatin(item.name as string).replace(/ /g, '_'))
					.join('_');
				reachGoal((InsuranceGoal.PurchaseWithInsurance + insurancesToAnalytics) as Goal);
			}

			WebskyAnalyticsApollo.sendOrderEvent(EventType.ConfirmationPage, orderAfterChange);
		}
	}

	private static analyzeGetCheckinOrderQuery(orderAfterChange: CheckinOrderFragment) {
		const orderBeforeChange = this.previousOrderState;

		if (
			orderBeforeChange?.status === CheckinOrderStatus.Started &&
			orderAfterChange.status === CheckinOrderStatus.Confirmed
		) {
			getOrderDataLayer().purchaseOrder(
				orderBeforeChange.travellers.map(traveller => traveller.pnrLocator).join(','),
				WebskyServicesAnalytics.getCheckinSelectedServices(orderBeforeChange) // orderBeforeChange for correct calculation additional services
			);
		}
	}

	private static analyzeCheckinCreateOrderQuery(order: CheckinOrderFragment) {
		if (order?.status === CheckinOrderStatus.New) {
			reachGoal(CheckinGoal.Create, { funnel_checkin: getFunnelParameters(order) });
		}
	}

	private static analyzeCheckinStartQuery(order: CheckinOrderFragment) {
		if (order?.status === CheckinOrderStatus.Started) {
			reachGoal(CheckinGoal.Start, { funnel_checkin: getFunnelParameters(order) });
			WebskyAnalyticsApollo.sendOrderEvent(EventType.InitiateCheckin, order);
		}
	}

	private static analyzeCheckinCompleteQuery(order: CheckinOrderFragment) {
		if (order.status === CheckinOrderStatus.Confirmed && !order.price.total?.amount) {
			reachGoal(CheckinGoal.CompleteWithoutPayment, { funnel_checkin: getFunnelParameters(order) });
		}
	}

	private static analyzeCheckinDocumentsQuery(data: CheckinDocumentsQuery['CheckinDocuments']) {
		const order = this.previousOrderState;

		if (data) {
			reachGoal(CheckinGoal.BoardingPassDownload, { funnel_checkin: getFunnelParameters(order) });
		}
	}

	private static analyzeSaveOrderServicesQuery(orderAfterChange: CheckoutCheckinOrder, ecommerceName?: string) {
		if (ecommerceName) {
			reachGoal(ecommerceName as Goal, { funnel_purchase: getFunnelParameters(orderAfterChange) });
		}

		const orderBeforeChange = this.previousOrderState;

		if (!orderBeforeChange) {
			return;
		}

		const added: DataLayerService[] = [];
		const removed: DataLayerService[] = [];

		const addedInfo: ChangedService[] = [];
		const removedInfo: ChangedService[] = [];

		const differs = [Gds, Seat, Aeroexpress, Nas];
		differs.forEach(Differ => {
			const differ = new Differ(orderBeforeChange, orderAfterChange);
			const diff = differ.diff();

			added.push(...diff.added);
			removed.push(...diff.removed);

			if (diff.addedInfo && diff.removedInfo) {
				addedInfo.push(...diff.addedInfo);
				removedInfo.push(...diff.removedInfo);
			}
		});

		if (addedInfo.length) {
			WebskyAnalyticsApollo.sendOrderEvent(EventType.AddToCart, orderAfterChange as CheckoutOrderFragment);
		}
		if (removedInfo.length) {
			WebskyAnalyticsApollo.sendOrderEvent(EventType.RemoveFromCart, orderAfterChange as CheckoutOrderFragment);
		}

		getOrderDataLayer().removeServicesFromCart(removed);
		getOrderDataLayer().addServicesToCart(added);
	}

	private static analyzeRunSearchMutation(searchParams: SearchParametersFragment) {
		const searchFormState = get<CachedSearchFormState>(SEARCH_FORM_STATE_KEY, {});
		WebskyAnalyticsApollo.sendSearchEvent(searchParams, searchFormState);
	}

	private static analyzeBookOrderMutation(order: CheckoutOrderFragment) {
		WebskyAnalyticsApollo.sendOrderEvent(EventType.AddShippingInfo, order);
	}

	private static analyzeCreateOrderMutation(order: CheckoutOrderFragment) {
		WebskyAnalyticsApollo.sendOrderEvent(EventType.AddToCart, order);
	}

	private static analyzePaymentMethodsQuery() {
		WebskyAnalyticsApollo.sendOrderEvent(EventType.BeginCheckout, this.previousOrderState as CheckoutOrderFragment);
	}

	private static analyzeCreateExchangeMutation() {
		WebskyAnalyticsApollo.sendOrderEvent(
			EventType.InitiateExchange,
			this.previousOrderState as CheckoutOrderFragment
		);
	}

	private static analyzeCreateRefundMutation() {
		WebskyAnalyticsApollo.sendOrderEvent(
			EventType.InitiateRefund,
			this.previousOrderState as CheckoutOrderFragment
		);
	}

	private static analyzeConfirmRefundMutation() {
		WebskyAnalyticsApollo.sendOrderEvent(EventType.Refund, this.previousOrderState as CheckoutOrderFragment);
	}

	private static analyzePriceFreezeView() {
		const freezeService = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.gdsServices?.services?.find(
			isFareLockService
		);
		if (freezeService) {
			getOrderDataLayer().setViewItem(
				getHoldDatalayerItem(freezeService, WebskyAnalyticsApollo.previousOrderState?.travellers)
			);
		}
	}

	private static analyzeViewItem(
		e: CustomEvent<WxEvent>,
		type: OrderAdditionalServiceGdsServiceServiceType.Meal | OrderAdditionalServiceGdsServiceServiceType.Baggage
	): void {
		const options = e.detail.options;

		if (!options?.servicesIds || !options?.segmentId) {
			return null;
		}

		const item = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.gdsServices?.services?.find(
			service =>
				options.servicesIds.includes(service.id) &&
				service.allowedSegments?.some(segmentsIds =>
					segmentsIds.some(segmentId => segmentId === options.segmentId)
				)
		);

		if (item) {
			getOrderDataLayer().setViewItem({
				category: getServiceCategory(type),
				name: item.name,
				price: item.price.amount,
				segmentId: options.segmentId,
				isRecommend: WebskyAnalyticsApollo.previousOrderState?.travellers?.some(traveller =>
					traveller.preselectedServices.gdsServices?.services?.some(service => {
						return service.serviceId === item.id;
					})
				),
				count: 1,
				travellersTypes: getTravellersTypes(WebskyAnalyticsApollo.previousOrderState?.travellers)
			});
		}
	}

	private static analyzeNASViewItem(e: CustomEvent<WxEvent>): void {
		const options = e.detail.options;

		if (!options?.servicesIds || !options?.segmentId) {
			return null;
		}

		const item = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.nas?.services?.find(
			service => options.servicesIds.includes(service.id) && service.segmentId.toString() === options.segmentId
		);

		if (item) {
			getOrderDataLayer().setViewItem({
				...WebskyServicesAnalytics.convertNasServiceToDataLayer(
					item,
					WebskyAnalyticsApollo.previousOrderState?.travellers
				),
				isRecommend: WebskyAnalyticsApollo.previousOrderState?.travellers?.some(traveller =>
					traveller.preselectedServices?.gdsServices?.services?.some(service => service.serviceId === item.id)
				)
			});
		}
	}

	private static analyzeAeroexpressView() {
		const availableServices =
			WebskyAnalyticsApollo.previousOrderState?.additionalServices?.transfer?.aeroexpress?.trips;

		if (availableServices) {
			getOrderDataLayer().setServicesViewItemList(
				DataLayerItemsListName.Aeroexpress,
				WebskyServicesAnalytics.aeroexpress(
					availableServices,
					WebskyAnalyticsApollo.previousOrderState?.travellers
				)
			);
		}
	}

	private static analyzePetsView() {
		const pets = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.gdsServices?.services?.filter(
			service =>
				animalBaggageTypes.includes((service as OrderAdditionalServiceGdsServiceBaggage).baggageType) &&
				service.canBeAdded
		);

		if (pets) {
			getOrderDataLayer().setServicesViewItemList(
				DataLayerItemsListName.Pets,
				WebskyServicesAnalytics.baggage(
					pets.map(service =>
						convertBaggageObject(service as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)
					),
					WebskyAnalyticsApollo.previousOrderState?.travellers
				)
			);
		}
	}

	private static analyzeInsuranceView(e: CustomEvent<WxEvent>): void {
		const options = e.detail.options as WxEventOptions;

		if (!options?.servicesIds) {
			return null;
		}

		const insurances = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.insurances?.insurancePrograms?.filter(
			insurance => options.servicesIds.includes(insurance.code)
		);

		if (insurances) {
			getOrderDataLayer().setServicesViewItemList(
				DataLayerItemsListName.Insurance,
				WebskyServicesAnalytics.insurances(insurances, WebskyAnalyticsApollo.previousOrderState?.travellers)
			);
		}
	}

	private static analyzeBaggageListView() {
		const baggages = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.gdsServices?.services?.filter(
			service =>
				service.type === OrderAdditionalServiceGdsServiceServiceType.Baggage &&
				!animalBaggageTypes.includes((service as OrderAdditionalServiceGdsServiceBaggage).baggageType) &&
				service.canBeAdded
		);

		const dataLayerBaggages: DataLayerService[] = [];
		(baggages as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment[])?.forEach(baggage => {
			baggage.allowedSegments?.forEach(segmentIds => {
				segmentIds?.forEach(segmentId => {
					dataLayerBaggages.push({
						name: getBaggageDatalayerName(baggage),
						price: baggage.price.amount,
						category: getServiceCategory(
							OrderAdditionalServiceGdsServiceServiceType.Baggage,
							baggage.baggageType
						),
						segmentId,
						count: 1,
						isRecommend: WebskyAnalyticsApollo.previousOrderState?.travellers?.some(traveller =>
							traveller.preselectedServices?.gdsServices?.services?.some(service => {
								return service.serviceId === baggage.id;
							})
						),
						travellersTypes: getTravellersTypes(WebskyAnalyticsApollo.previousOrderState?.travellers)
					});
				});
			});
		});

		getOrderDataLayer().setServicesViewItemList(DataLayerItemsListName.Baggage, dataLayerBaggages);
	}

	private static analyzeMealsListView(e: CustomEvent<WxEvent>) {
		const options = e.detail.options as WxEventOptions;

		if (!options?.segmentId) {
			return;
		}

		const meals = WebskyAnalyticsApollo.previousOrderState?.additionalServices?.gdsServices?.services?.filter(
			service =>
				service.type === OrderAdditionalServiceGdsServiceServiceType.Meal &&
				service.allowedSegments.some(segmentsIds =>
					segmentsIds.some(segmentId => segmentId === options.segmentId)
				) &&
				service.canBeAdded
		);

		const dataLayerMeals: DataLayerService[] = [];
		(meals as GdsService_OrderAdditionalServiceGdsServiceMeal_Fragment[])?.forEach(meal => {
			dataLayerMeals.push({
				category: getServiceCategory(OrderAdditionalServiceGdsServiceServiceType.Meal),
				name: meal.name,
				price: meal.price.amount,
				segmentId: options.segmentId,
				isRecommend: WebskyAnalyticsApollo.previousOrderState?.travellers?.some(traveller =>
					traveller.preselectedServices.gdsServices?.services?.some(service => {
						return service.serviceId === meal.id;
					})
				),
				count: 1,
				travellersTypes: getTravellersTypes(WebskyAnalyticsApollo.previousOrderState?.travellers)
			});
		});

		getOrderDataLayer().setServicesViewItemList(DataLayerItemsListName.Meal, dataLayerMeals);
	}

	private static analyzeSeatsListView(e: CustomEvent<WxEvent>) {
		const options = e.detail.options as WxEventOptions;

		if (!options?.segmentId) {
			return;
		}

		const seats = WebskyAnalyticsApollo.isCheckoutOrderGuard(WebskyAnalyticsApollo.previousOrderState)
			? WebskyAnalyticsApollo.previousOrderState?.flight?.seatMap?.segments?.[options.segmentId]
			: WebskyAnalyticsApollo.previousOrderState?.seatMap?.segments?.[options.segmentId];
		const travellers = WebskyAnalyticsApollo.previousOrderState?.travellers;

		if (seats && travellers) {
			getOrderDataLayer().setServicesViewItemList(
				DataLayerItemsListName.Seats,
				WebskyServicesAnalytics.seats(seats, travellers)
			);
		}
	}

	private static analyzeVipListView(e: CustomEvent<WxEvent>): void {
		const options = e.detail.options as WxEventOptions;
		const vipServices: DataLayerService[] = [];

		if (!options?.subType) {
			return null;
		}

		WebskyAnalyticsApollo.previousOrderState?.additionalServices?.gdsServices?.services?.forEach(service => {
			const typedService = service as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment;

			const isAvailableType = [
				OrderAdditionalServiceGdsServiceServiceType.Vip,
				OrderAdditionalServiceGdsServiceServiceType.BusinessLounge
			].includes(typedService.type);
			const isSameType = typedService.subType === options.subType;

			if (typedService.canBeAdded && isAvailableType && isSameType) {
				service.allowedPassengers.forEach(ids => {
					vipServices.push(
						WebskyServicesAnalytics.convertVipToLayerService(
							typedService,
							ids[0],
							WebskyAnalyticsApollo.previousOrderState?.travellers
						)
					);
				});
			}
		});

		if (vipServices.length) {
			getOrderDataLayer().setServicesViewItemList(
				vipServices[0].category === ItemCategory.Business ? DataLayerItemsListName.Business : null,
				vipServices
			);
		}
	}

	private static analyzeNASVipListView(e: CustomEvent<WxEvent>): void {
		const options = e.detail.options as WxEventOptions;
		const vipServices: DataLayerService[] = [];

		if (!options?.nasType) {
			return null;
		}

		WebskyAnalyticsApollo.previousOrderState?.additionalServices?.nas?.services?.forEach(service => {
			const isIncludeIds = !options?.servicesIds || options.servicesIds.includes(service.id);
			const isAvailableType = service.type === NASProductType.Lounge;
			const isSameType = service.type === options.nasType;

			if (service.canBeAdded && isIncludeIds && isAvailableType && isSameType) {
				vipServices.push(
					WebskyServicesAnalytics.convertNasServiceToDataLayer(
						service,
						WebskyAnalyticsApollo.previousOrderState?.travellers
					)
				);
			}
		});

		if (vipServices.length) {
			getOrderDataLayer().setServicesViewItemList(
				vipServices[0].category === ItemCategory.Business ? DataLayerItemsListName.Business : null,
				vipServices
			);
		}
	}

	private static analyzeAiPurchaseModalView(e: CustomEvent<WxEvent>, action?: PurchaseShowPopupAction): void {
		const options = e.detail.options;

		if (!options?.servicesIds) {
			return null;
		}

		const order = WebskyAnalyticsApollo.previousOrderState;
		const serviceItem = order?.additionalServices?.gdsServices?.services?.find(service =>
			options.servicesIds.includes(service.id)
		);

		if (!serviceItem) {
			return;
		}

		const event = getAiPurchasePopupGoal(serviceItem.type, action) as Goal;
		reachGoal(event, { funnel_purchase: getFunnelParameters(order) });
	}

	private static analyzeWxViewTypeWrapper(e: Event) {
		WebskyAnalyticsApollo.analyzeWxView(e as CustomEvent);
	}

	private static analyzeWxView(e: CustomEvent<WxEvent>) {
		switch (e.detail.itemCategory) {
			case WxEvents.PriceFreeze:
				WebskyAnalyticsApollo.analyzePriceFreezeView();
				break;

			case WxEvents.MealItem:
				WebskyAnalyticsApollo.analyzeViewItem(e, OrderAdditionalServiceGdsServiceServiceType.Meal);
				break;

			case WxEvents.AeroexpressList:
				WebskyAnalyticsApollo.analyzeAeroexpressView();
				break;

			case WxEvents.PetsList:
				WebskyAnalyticsApollo.analyzePetsView();
				break;

			case WxEvents.InsurancesList:
				WebskyAnalyticsApollo.analyzeInsuranceView(e);
				break;

			case WxEvents.BaggageList:
				WebskyAnalyticsApollo.analyzeBaggageListView();
				break;

			case WxEvents.BaggageItem:
				WebskyAnalyticsApollo.analyzeViewItem(e, OrderAdditionalServiceGdsServiceServiceType.Baggage);
				break;

			case WxEvents.MealsList:
				WebskyAnalyticsApollo.analyzeMealsListView(e);
				break;

			case WxEvents.SeatsList:
				WebskyAnalyticsApollo.analyzeSeatsListView(e);
				break;

			case WxEvents.VipList:
				WebskyAnalyticsApollo.analyzeVipListView(e);
				break;

			case WxEvents.NASVipList:
				WebskyAnalyticsApollo.analyzeNASVipListView(e);
				break;

			case WxEvents.NASVipItem:
				WebskyAnalyticsApollo.analyzeNASViewItem(e);
				break;

			case WxEvents.AiPurchaseModalOpen:
				WebskyAnalyticsApollo.analyzeAiPurchaseModalView(e);
				break;

			case WxEvents.AiPurchaseModalClose:
				WebskyAnalyticsApollo.analyzeAiPurchaseModalView(e, PurchaseShowPopupAction.Decline);
				break;

			default:
				break;
		}
	}

	private static setPreviousOrderState(
		operation: Operation,
		response: ApolloResponse,
		client: ApolloClient<NormalizedCacheObject>
	) {
		// key - operationName, value - path to order in response
		const initializationQueriesOperationNameAndPath = {
			CreateOrder: 'CreateOrder',
			FindOrder: 'FindOrder',
			Order: 'Order',
			GetCheckinOrder: 'CheckinOrder',
			CheckinCreateOrder: 'CheckinCreateOrder',
			CheckinStart: 'CheckinStart'
		};

		const currentInitializationQuery = Object.entries(initializationQueriesOperationNameAndPath).find(
			([name, path]) => operation.operationName === name && response.data[path]
		);

		const orderId: string =
			response.data[operation.operationName === 'GetCheckinOrder' ? 'CheckinOrder' : operation.operationName]
				?.id ?? (operation.operationName === 'PaymentMethods' ? operation.variables.parameters?.orderId : null);

		const cachedCheckoutOrder = WebskyAnalyticsApollo.readCachedOrder(
			client,
			orderId,
			'checkout'
		) as CheckoutOrderFragment;

		const cachedCheckinOrder = WebskyAnalyticsApollo.readCachedOrder(
			client,
			orderId,
			'checkin'
		) as CheckinOrderFragment;

		if (currentInitializationQuery && !(cachedCheckoutOrder || cachedCheckinOrder)) {
			const [, pathToOrder] = currentInitializationQuery;
			this.previousOrderState = response.data[pathToOrder];
		} else {
			if (cachedCheckoutOrder || cachedCheckinOrder) {
				this.previousOrderState = cachedCheckoutOrder ?? cachedCheckinOrder;
			}
		}
	}

	private static setGraphqlUrlOnce(operation: Operation) {
		if (!this.graphqlUrl) {
			this.graphqlUrl = operation.getContext()?.response?.url;
		}
	}

	public static listenWxViewEvent() {
		document.removeEventListener(this.wxView, this.analyzeWxViewTypeWrapper);
		document.addEventListener(this.wxView, this.analyzeWxViewTypeWrapper);
	}

	public static dispatchWxViewEvent(itemCategory: WxEvents, options?: WxEventOptions) {
		const event = new CustomEvent(this.wxView, { detail: { itemCategory, options } });
		document.dispatchEvent(event);
	}

	public static analyze(operation: Operation, response: ApolloResponse, client: ApolloClient<NormalizedCacheObject>) {
		if (!response?.data) {
			return;
		}

		WebskyAnalyticsApollo.setPreviousOrderState(operation, response, client);
		WebskyAnalyticsApollo.setGraphqlUrlOnce(operation);

		switch (operation.operationName) {
			case 'Order':
				this.analyzeOrderQuery(response.data.Order);
				break;

			case 'SaveOrderServices':
				this.analyzeSaveOrderServicesQuery(
					response.data.SaveOrderServices,
					operation.getContext().custom_ecommerce_name
				);
				break;

			case 'CheckinSaveOrderServices':
				this.analyzeSaveOrderServicesQuery(response.data.CheckinSaveOrderServices);
				break;

			case 'GetCheckinOrder':
				this.analyzeGetCheckinOrderQuery(response.data.CheckinOrder);
				break;

			case 'CheckinCreateOrder':
				this.analyzeCheckinCreateOrderQuery(response.data.CheckinCreateOrder);
				break;

			case 'CheckinStart':
				this.analyzeCheckinStartQuery(response.data.CheckinStart);
				break;

			case 'CheckinComplete':
				this.analyzeCheckinCompleteQuery(response.data.CheckinComplete);
				break;

			case 'CheckinDocuments':
				this.analyzeCheckinDocumentsQuery(response.data.CheckinDocuments);
				break;

			case 'RunSearch':
				this.analyzeRunSearchMutation(operation.variables.params);
				break;

			case 'CreateOrder':
				this.analyzeCreateOrderMutation(response.data.CreateOrder);
				break;

			case 'BookOrder':
				this.analyzeBookOrderMutation(response.data.BookOrder);
				break;

			case 'PaymentMethods':
				this.analyzePaymentMethodsQuery();
				break;

			case 'CreateExchange':
				this.analyzeCreateExchangeMutation();
				break;

			case 'createRefundOrder':
				this.analyzeCreateRefundMutation();
				break;

			case 'confirmRefundOrder':
				this.analyzeConfirmRefundMutation();
				break;
		}
	}

	public static setUsePixel(usePixel: boolean) {
		this.usePixel = usePixel;
	}
}

export default WebskyAnalyticsApollo;
